import { useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  BookOpenCheck,
  BrainCircuit,
  Building2,
  ChevronDown,
  ChevronRight,
  Database,
  FileStack,
  Handshake,
  History,
  KeyRound,
  LayoutDashboard,
  LibraryBig,
  MoreHorizontal,
  PanelsTopLeft,
  ShieldCheck,
  X,
} from "lucide-react";
import { getTemplateOverridesWithDefaults } from "../lib/api";
import { classNames } from "../lib/format";
import { submodulesWithVisibleCounts, withTemplateSubmodule } from "../lib/templateVisibility";
import { ServiceBadge } from "./Shared";

const clientItems = [
  { id: "templates", label: "文书模板" },
  { id: "risk", label: "风险预防" },
  { id: "commercial", label: "商业往来" },
];

const expertItems = [
  { id: "review", label: "审校工作台", icon: BadgeCheck },
  { id: "rules", label: "规则知识库", icon: BookOpenCheck },
  { id: "cases", label: "案例沉淀", icon: History },
];

const platformItems = [
  { id: "overview", label: "平台总览", icon: LayoutDashboard },
  { id: "portals", label: "端口管理", icon: PanelsTopLeft },
  { id: "assets", label: "模板资产", icon: Database },
  { id: "intelligence", label: "AI 成长引擎", icon: BrainCircuit },
  { id: "tenants", label: "租户策略", icon: Building2 },
  { id: "permissions", label: "权限审计", icon: KeyRound },
];

const SIDEBAR_VISIBILITY_STORAGE_PREFIX = "document-hub-sidebar-visibility-v1:";
// 本分系统独立维护页面授权版本号，同一分系统不同端口页面保持一致，供授权管理读取当前 Git HEAD 时提取。
// 中文维护注释：15位规则为北京时间日期 + 实际修改电脑编号 + 固定项目编号12 + 当天三位序号。
const SUBSYSTEM_VERSION_LABEL = "202610176312000";

function readVisibility(role, items) {
  // 各角色分别保存导航可见性，切换端口时不会覆盖彼此的个性化设置。
  const defaults = Object.fromEntries(items.map((item) => [item.id, true]));
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(SIDEBAR_VISIBILITY_STORAGE_PREFIX + role) || "{}",
    );
    items.forEach((item) => {
      if (typeof stored[item.id] === "boolean") defaults[item.id] = stored[item.id];
    });
  } catch {
    return defaults;
  }
  return defaults;
}

function SidebarDisplaySettings({ items, open, visibility, onToggle, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="sidebar-settings-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="sidebar-settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sidebar-settings-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="sidebar-settings-title">左侧导航栏显示设置</h2>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭显示设置">
            <X size={18} />
          </button>
        </header>
        <div className="sidebar-settings-table-wrap">
          <table className="sidebar-settings-table">
            <thead>
              <tr>
                <th scope="col">序号</th>
                <th scope="col">项目</th>
                <th scope="col">显示开关</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{item.label}</td>
                  <td>
                    <label className="visibility-switch">
                      <input
                        type="checkbox"
                        checked={visibility[item.id] !== false}
                        onChange={() => onToggle(item.id)}
                        aria-label={"显示" + item.label}
                      />
                      <span aria-hidden="true" />
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default function WorkspaceSidebar({
  role,
  roleView,
  onRoleViewChange,
  catalog,
  catalogStatus,
  serviceStatus,
  clientDomain,
  selectedModuleKey,
  selectedSubmoduleKey,
  onClientDomainChange,
  onModuleChange,
  onSubmoduleChange,
  moduleData,
  overviewModulesByKey = {},
  companyRegion,
  company,
  open,
  onClose,
}) {
  const [riskExpanded, setRiskExpanded] = useState(false);
  const [commercialExpanded, setCommercialExpanded] = useState(clientDomain === "commercial");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsItems = useMemo(() => {
    if (role === "expert") return expertItems;
    if (role === "platform") return platformItems;
    return clientItems;
  }, [role]);
  const [visibility, setVisibility] = useState(() => readVisibility(role, settingsItems));
  const [expandedRiskModuleKey, setExpandedRiskModuleKey] = useState("");
  const [overrideTemplatesByModule, setOverrideTemplatesByModule] = useState({});
  const [latestChangeLog, setLatestChangeLog] = useState(null);
  const [changeLogStatus, setChangeLogStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();
    // 中文维护注释：修改日志仅以版本化 JSON 为唯一来源，禁止在组件中复制静态数组，避免旧代码覆盖最新记录。
    fetch("/data/subsystem-change-log.json", {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("修改日志请求失败：" + response.status);
        return response.json();
      })
      .then((payload) => {
        setLatestChangeLog(Array.isArray(payload.logs) ? payload.logs[0] || null : null);
        setChangeLogStatus("ready");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setLatestChangeLog(null);
        setChangeLogStatus("error");
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (clientDomain === "risk") setRiskExpanded(true);
    if (clientDomain === "commercial") setCommercialExpanded(true);
  }, [clientDomain]);

  useEffect(() => {
    setVisibility(readVisibility(role, settingsItems));
  }, [role, settingsItems]);

  const riskModules = useMemo(
    () => (catalog?.modules || []).filter((module) => module.moduleKey !== "commercial-exchange"),
    [catalog],
  );
  const commercialModule = (catalog?.modules || []).find(
    (module) => module.moduleKey === "commercial-exchange",
  );
  const overrideModuleKey = role === "client"
    ? clientDomain === "commercial"
      ? (commercialExpanded ? "commercial-exchange" : "")
      : riskExpanded
        ? (expandedRiskModuleKey || (clientDomain === "risk" ? selectedModuleKey : ""))
        : ""
    : "";
  const overrideModuleSummary = (catalog?.modules || []).find(
    (module) => module.moduleKey === overrideModuleKey,
  );
  const overrideModuleData = overrideModuleKey
    ? overviewModulesByKey[overrideModuleKey]
      || (moduleData?.moduleKey === overrideModuleKey ? moduleData : overrideModuleSummary)
    : null;

  useEffect(() => {
    if (!overrideModuleKey || !overrideModuleSummary) return undefined;
    const controller = new AbortController();
    getTemplateOverridesWithDefaults(
      {
        moduleKey: overrideModuleSummary.moduleKey,
        moduleName: overrideModuleSummary.moduleName,
        companyId: company?.id,
        companyName: company?.name,
      },
      controller.signal,
    )
      .then((payload) => {
        const normalizedTemplates = (payload.templates || []).map((template) =>
          withTemplateSubmodule(template, overrideModuleData || overrideModuleSummary),
        );
        setOverrideTemplatesByModule((current) => ({
          ...current,
          [overrideModuleKey]: normalizedTemplates,
        }));
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setOverrideTemplatesByModule((current) => ({
          ...current,
          [overrideModuleKey]: [],
        }));
      });
    return () => controller.abort();
  }, [
    company?.id,
    company?.name,
    overrideModuleData,
    overrideModuleKey,
    overrideModuleSummary,
  ]);

  function toggleVisibility(itemId) {
    setVisibility((current) => {
      const next = {
        ...current,
        [itemId]: current[itemId] === false,
      };
      try {
        window.localStorage.setItem(
          SIDEBAR_VISIBILITY_STORAGE_PREFIX + role,
          JSON.stringify(next),
        );
      } catch {
        // 本地存储不可用时仍更新当前会话，不让浏览器策略阻断用户操作。
      }
      return next;
    });
  }

  function isVisible(itemId) {
    return visibility[itemId] !== false;
  }

  return (
    <>
      <aside className={classNames("workspace-sidebar", open && "open")} aria-label="主导航">
        <div className="sidebar-mobile-controls">
          <button className="icon-button sidebar-close" type="button" onClick={onClose} aria-label="关闭导航">
            <X size={18} />
          </button>
        </div>

        <nav className="sidebar-navigation">
          {role === "client" ? (
            <div className="nav-section">
              {isVisible("templates") ? (
                <button
                  className={classNames("nav-entry", clientDomain === "templates" && "active")}
                  type="button"
                  onClick={() => {
                    onClientDomainChange("templates");
                    onClose();
                  }}
                >
                  <FileStack size={17} aria-hidden="true" />
                  <span>文书模板</span>
                </button>
              ) : null}

              {isVisible("risk") ? (
                <button
                  className={classNames("nav-entry", clientDomain === "risk" && "active")}
                  type="button"
                  onClick={() => {
                    const nextExpanded = !riskExpanded;
                    setRiskExpanded(nextExpanded);
                    if (!nextExpanded) setExpandedRiskModuleKey("");
                    onClientDomainChange("risk");
                  }}
                >
                  <ShieldCheck size={17} aria-hidden="true" />
                  <span>风险预防</span>
                  {riskExpanded ? <ChevronDown className="nav-chevron" size={15} /> : <ChevronRight className="nav-chevron" size={15} />}
                </button>
              ) : null}
              {isVisible("risk") && riskExpanded ? (
              <div className="nav-tree" aria-label="风险预防目录">
                {riskModules.map((module) => {
                  const moduleActive =
                    selectedModuleKey === module.moduleKey && clientDomain === "risk";
                  const moduleExpanded = expandedRiskModuleKey === module.moduleKey;
                  const countModuleData =
                    overviewModulesByKey[module.moduleKey]
                    || (moduleData?.moduleKey === module.moduleKey ? moduleData : module);
                  const visibleSubmodules = submodulesWithVisibleCounts(
                    countModuleData,
                    companyRegion,
                    overrideTemplatesByModule[module.moduleKey] || [],
                  );
                  const visibleModuleFileCount = visibleSubmodules.reduce(
                    (sum, submodule) => sum + (submodule.visibleFileCount || 0),
                    0,
                  );
                  return (
                    <div className="nav-tree-module" key={module.moduleKey}>
                      <button
                        className={moduleActive ? "active" : ""}
                        type="button"
                        aria-expanded={moduleExpanded}
                        onClick={() => {
                          setExpandedRiskModuleKey((current) =>
                            current === module.moduleKey ? "" : module.moduleKey,
                          );
                          onModuleChange(module.moduleKey, "risk");
                        }}
                      >
                        <span className="nav-tree-module-label">{module.moduleName}</span>
                        <span className="nav-tree-meta">
                          <small>{visibleModuleFileCount}</small>
                          {visibleSubmodules.length ? (
                            moduleExpanded ? (
                              <ChevronDown size={13} aria-hidden="true" />
                            ) : (
                              <ChevronRight size={13} aria-hidden="true" />
                            )
                          ) : null}
                        </span>
                      </button>
                      {moduleExpanded ? (
                        <div
                          className="nav-subtree"
                          aria-label={module.moduleName + "具体子项目"}
                        >
                          {visibleSubmodules.map((submodule) => (
                            <button
                              className={
                                selectedSubmoduleKey === submodule.submoduleKey
                                  ? "active"
                                  : ""
                              }
                              key={submodule.submoduleKey}
                              type="button"
                              onClick={() => {
                                onModuleChange(module.moduleKey, "risk");
                                onSubmoduleChange(submodule.submoduleKey);
                                onClose();
                              }}
                            >
                              <span>{submodule.submoduleName}</span>
                              <small>{submodule.visibleFileCount ?? submodule.fileCount ?? 0}</small>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

              {isVisible("commercial") ? (
                <button
                  className={classNames("nav-entry", clientDomain === "commercial" && "active")}
                  type="button"
                  onClick={() => {
                    setCommercialExpanded((value) => !value);
                    onClientDomainChange("commercial");
                  }}
                >
                  <Handshake size={17} aria-hidden="true" />
                  <span>商业往来</span>
                  {commercialExpanded ? <ChevronDown className="nav-chevron" size={15} /> : <ChevronRight className="nav-chevron" size={15} />}
                </button>
              ) : null}
              {isVisible("commercial") && commercialExpanded && commercialModule ? (
              <div className="nav-tree" aria-label="商业往来目录">
                {submodulesWithVisibleCounts(
                  overviewModulesByKey["commercial-exchange"]
                    || (moduleData?.moduleKey === "commercial-exchange" ? moduleData : commercialModule),
                  companyRegion,
                  overrideTemplatesByModule["commercial-exchange"] || [],
                ).map((submodule) => (
                  <button
                    className={
                      clientDomain === "commercial" &&
                      selectedSubmoduleKey === submodule.submoduleKey
                        ? "active"
                        : ""
                    }
                    key={submodule.submoduleKey}
                    type="button"
                    onClick={() => {
                      onModuleChange("commercial-exchange", "commercial");
                      onSubmoduleChange(submodule.submoduleKey);
                      onClose();
                    }}
                  >
                    <span>{submodule.submoduleName}</span>
                    <small>{submodule.visibleFileCount ?? submodule.fileCount ?? 0}</small>
                  </button>
                ))}
              </div>
            ) : null}
            </div>
          ) : null}

          {role === "expert" ? (
            <div className="nav-section">
              {expertItems.filter((item) => isVisible(item.id)).map((item) => {
              const Icon = item.icon;
              return (
                <button
                  className={classNames("nav-entry", roleView === item.id && "active")}
                  key={item.id}
                  type="button"
                  onClick={() => {
                    onRoleViewChange(item.id);
                    onClose();
                  }}
                >
                  <Icon size={17} aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            </div>
          ) : null}

          {role === "platform" ? (
            <div className="nav-section">
              {platformItems.filter((item) => isVisible(item.id)).map((item) => {
              const Icon = item.icon;
              return (
                <button
                  className={classNames("nav-entry", roleView === item.id && "active")}
                  key={item.id}
                  type="button"
                  onClick={() => {
                    onRoleViewChange(item.id);
                    onClose();
                  }}
                >
                  <Icon size={17} aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            </div>
          ) : null}
        </nav>

        <div className="sidebar-footer">
          <div className="source-line">
            <LibraryBig size={16} aria-hidden="true" />
            <div>
              <strong>{catalogStatus === "ready" ? (catalog?.fileCount || 0).toLocaleString("zh-CN") + " 份模板" : "读取模板目录"}</strong>
              <span>来源：用工风险管控系统</span>
            </div>
          </div>
          <ServiceBadge
            online={serviceStatus === "online"}
            checking={serviceStatus === "checking"}
            label={serviceStatus === "online" ? "AI 与业务服务在线" : "AI 使用本地辅助模式"}
          />
        </div>

        <div className="sidebar-more">
          <button
            className="sidebar-more-button"
            type="button"
            onClick={() => setSettingsOpen(true)}
            aria-haspopup="dialog"
          >
            <MoreHorizontal size={17} aria-hidden="true" />
            <span>更多...</span>
          </button>
        </div>

        <div className="sidebar-project-version" title={"版本号 " + SUBSYSTEM_VERSION_LABEL} data-subsystem-version={SUBSYSTEM_VERSION_LABEL}>
          {/* 与四字导航文案左边缘对齐，便于在左侧页面最低处快速核对当前变更版本。 */}
          <span>版本号</span>
          <strong>{SUBSYSTEM_VERSION_LABEL}</strong>
          <small data-subsystem-change-log>
            {changeLogStatus === "loading"
              ? "正在读取最新修改日志..."
              : changeLogStatus === "error"
                ? "修改日志读取失败"
                : latestChangeLog
                  ? `最新修改日志：${latestChangeLog.marker || "版本更新"} · ${latestChangeLog.optimization}`
                  : "暂无修改日志"}
          </small>
        </div>
      </aside>

      <SidebarDisplaySettings
        items={settingsItems}
        open={settingsOpen}
        visibility={visibility}
        onToggle={toggleVisibility}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}
